import { Button, Result, Spin } from 'antd'
import { useGetBillingPortalQuery } from 'features/settings/api'
import { globalToaster } from 'components/elements/global'
import React, { FC, useEffect, useRef, useState } from 'react'

const BillingPortal: FC = () => {
 const [localError, setLocalError] = useState<string>('')
 const [opened, setOpened] = useState(false)
 const hasOpenedRef = useRef(false)
 const { data, isError, error, isLoading, refetch } = useGetBillingPortalQuery(
  null,
  {
   refetchOnMountOrArgChange: true,
   refetchOnFocus: true,
   refetchOnReconnect: true,
  },
 )

 useEffect(() => {
  if (!hasOpenedRef.current && data) {
   if (typeof data !== 'string' || !data.trim()) {
    const message = 'Billing portal URL not available from server.'
    setLocalError(message)
    globalToaster({ content: message, type: 'error' })
    return
   }

   const popup = window.open(data, '_blank', 'noopener,noreferrer')
   if (!popup) {
    const message =
     'Unable to open billing portal in a new tab (popup blocked). Please check your browser popup settings.'
    setLocalError(message)
    globalToaster({ content: message, type: 'error' })
   } else {
    popup.focus()
    setLocalError('')
    setOpened(true)
    globalToaster({
     content: 'Billing portal opened in a new tab.',
     type: 'success',
    })
   }
   hasOpenedRef.current = true
  }
 }, [data])

 useEffect(() => {
  if (isError && !opened) {
   const message =
    (error as any)?.data?.message ||
    (error as any)?.error ||
    'Failed to fetch billing portal link. Please try again.'
   setLocalError(message)
   globalToaster({ content: message, type: 'error' })
  }
 }, [isError, error, opened])

 const handleRetry = (): void => {
  setLocalError('')
  hasOpenedRef.current = false
  setOpened(false)
  refetch()
 }

 const openUrl = (target: '_blank' | '_self'): void => {
  if (data) window.open(data, target, 'noopener,noreferrer')
 }

 if (isLoading) {
  return (
   <div className="d-flex justify-content-center align-items-center h-100">
    <Spin />
   </div>
  )
 }

 if (data) {
  return (
   <div className="d-flex justify-content-center align-items-center h-100">
    <Result
     status="success"
     title="Billing portal opened"
     subTitle="The billing portal is open in a new tab. If popup is blocked, open manually."
     extra={[
      <Button key="new" onClick={() => openUrl('_blank')}>
       Open in new tab
      </Button>,
      <Button key="self" onClick={() => openUrl('_self')}>
       Open in current tab
      </Button>,
     ]}
    />
   </div>
  )
 }

 if (localError) {
  return (
   <Result
    status="error"
    title="Unable to open billing portal"
    subTitle={localError}
    extra={
     <Button type="primary" key="retry" onClick={handleRetry}>
      Retry
     </Button>
    }
   />
  )
 }

 return (
  <div className="d-flex justify-content-center align-items-center h-100">
   <Spin />
  </div>
 )
}

export default BillingPortal
